import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db, storage } from '@/lib/firebase';
import { collection, getDocs, addDoc, doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

type CategoryKey = string;

interface Product {
  id?: string;
  category: CategoryKey;
  categoryId?: string;
  categoryName?: string;
  title: string;
  desc: string;
  price: string;
  image: string;
  cta: string;
  link: string;
  badge?: string;
}

const ProductsSection = () => {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [activeCategory, setActiveCategory] = useState<CategoryKey>('courses');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  
  // Estado para el archivo de imagen
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Formulario para nuevo producto
  const [newProduct, setNewProduct] = useState<Omit<Product, 'id'>>({
    category: 'courses',
    categoryId: 'courses',
    categoryName: 'Cursos',
    title: '',
    desc: '',
    price: '',
    image: 'https://readdy.ai/api/search-image?query=digital%20product%20neon%20glow&width=400&height=280',
    cta: 'Comprar Ahora',
    link: 'https://stan.store',
    badge: ''
  });

  const [categories, setCategories] = useState<{ key: string; label: string }[]>([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const prodSnap = await getDocs(collection(db, 'products'));
        const catSnap = await getDocs(collection(db, 'categories'));

        const productsData: Product[] = [];
        prodSnap.forEach((doc) => {
          productsData.push({ id: doc.id, ...doc.data() } as Product);
        });

        const fetchedCats = catSnap.docs.map((d) => ({
          key: d.id,
          label: d.data().name
        }));

        setCategories(fetchedCats);
        
        if (fetchedCats.length > 0) {
          setActiveCategory(fetchedCats[0].key);
        }

        if (productsData.length === 0) {
          const initialProducts: Product[] = [
            {
              category: 'courses',
              categoryId: 'courses',
              categoryName: t('home.products.categories.courses'),
              title: t('home.products.items.socialMastery.title'),
              desc: t('home.products.items.socialMastery.desc'),
              price: '$97',
              image: 'https://readdy.ai/api/search-image?query=online%20course%20social%20media%20marketing%20digital%20education%20laptop%20screen%20glowing%20purple%20blue%20neon%20modern%20workspace%20professional&width=400&height=280&seq=prod1&orientation=landscape',
              cta: t('home.products.items.socialMastery.cta'),
              link: 'https://stan.store',
              badge: t('home.products.items.socialMastery.badge'),
            },
            {
              category: 'courses',
              categoryId: 'courses',
              categoryName: t('home.products.categories.courses'),
              title: t('home.products.items.aiAutomation.title'),
              desc: t('home.products.items.aiAutomation.desc'),
              price: '$147',
              image: 'https://readdy.ai/api/search-image?query=artificial%20intelligence%20business%20automation%20robot%20digital%20tools%20futuristic%20purple%20blue%20glowing%20technology%20modern&width=400&height=280&seq=prod2&orientation=landscape',
              cta: t('home.products.items.aiAutomation.cta'),
              link: 'https://stan.store',
              badge: t('home.products.items.aiAutomation.badge'),
            },
            {
              category: 'products',
              categoryId: 'products',
              categoryName: t('home.products.categories.products'),
              title: t('home.products.items.contentCalendar.title'),
              desc: t('home.products.items.contentCalendar.desc'),
              price: '$27',
              image: 'https://readdy.ai/api/search-image?query=content%20calendar%20digital%20planner%20template%20social%20media%20schedule%20organized%20colorful%20modern%20design%20purple&width=400&height=280&seq=prod3&orientation=landscape',
              cta: t('home.products.items.contentCalendar.cta'),
              link: 'https://stan.store',
            },
            {
              category: 'products',
              categoryId: 'products',
              categoryName: t('home.products.categories.products'),
              title: t('home.products.items.brandKit.title'),
              desc: t('home.products.items.brandKit.desc'),
              price: '$47',
              image: 'https://readdy.ai/api/search-image?query=brand%20kit%20design%20templates%20logo%20colors%20typography%20digital%20product%20modern%20elegant%20purple%20blue%20gradient&width=400&height=280&seq=prod4&orientation=landscape',
              cta: t('home.products.items.brandKit.cta'),
              link: 'https://stan.store',
            },
            {
              category: 'workshops',
              categoryId: 'workshops',
              categoryName: t('home.products.categories.workshops'),
              title: t('home.products.items.launchBusiness.title'),
              desc: t('home.products.items.launchBusiness.desc'),
              price: '$67',
              image: 'https://readdy.ai/api/search-image?query=online%20workshop%20webinar%20live%20class%20digital%20business%20launch%20entrepreneur%20latina%20woman%20teaching%20screen&width=400&height=280&seq=prod5&orientation=landscape',
              cta: t('home.products.items.launchBusiness.cta'),
              link: 'https://stan.store',
              badge: t('home.products.items.launchBusiness.badge'),
            },
            {
              category: 'workshops',
              categoryId: 'workshops',
              categoryName: t('home.products.categories.workshops'),
              title: t('home.products.items.emailMarketing.title'),
              desc: t('home.products.items.emailMarketing.desc'),
              price: '$37',
              image: 'https://readdy.ai/api/search-image?query=email%20marketing%20masterclass%20digital%20course%20inbox%20automation%20funnel%20purple%20blue%20modern%20technology&width=400&height=280&seq=prod6&orientation=landscape',
              cta: t('home.products.items.emailMarketing.cta'),
              link: 'https://stan.store',
            },
            {
              category: 'services',
              categoryId: 'services',
              categoryName: t('home.products.categories.services'),
              title: t('home.products.items.consulting1on1.title'),
              desc: t('home.products.items.consulting1on1.desc'),
              price: '$197',
              image: 'https://readdy.ai/api/search-image?query=one%20on%20one%20business%20consulting%20strategy%20session%20professional%20woman%20laptop%20video%20call%20modern%20office%20purple%20lighting&width=400&height=280&seq=prod7&orientation=landscape',
              cta: t('home.products.items.consulting1on1.cta'),
              link: 'https://stan.store',
              badge: t('home.products.items.consulting1on1.badge'),
            },
            {
              category: 'services',
              categoryId: 'services',
              categoryName: t('home.products.categories.services'),
              title: t('home.products.items.socialManagement.title'),
              desc: t('home.products.items.socialManagement.desc'),
              price: `$497${t('home.products.items.socialManagement.priceSuffix')}`,
              image: 'https://readdy.ai/api/search-image?query=social%20media%20management%20service%20professional%20team%20content%20creation%20instagram%20facebook%20tiktok%20purple%20blue%20modern&width=400&height=280&seq=prod8&orientation=landscape',
              cta: t('home.products.items.socialManagement.cta'),
              link: 'https://stan.store',
            },
          ];

          for (const prod of initialProducts) {
            await addDoc(collection(db, 'products'), prod);
          }
          setProducts(initialProducts);
        } else {
          setProducts(productsData);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [t]);

  const handleCreateProduct = async () => {
    setUploading(true);
    try {
      let imageUrl = newProduct.image;
      
      // Si hay un archivo seleccionado, lo subimos a Storage
      if (imageFile) {
        const storageRef = ref(storage, `products/${Date.now()}_${imageFile.name}`);
        const snapshot = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      const selectedCat = categories.find(c => c.key === newProduct.category);
      const productToSave = {
        ...newProduct,
        image: imageUrl,
        categoryId: newProduct.category,
        categoryName: selectedCat ? selectedCat.label : newProduct.category
      };
      
      const docRef = await addDoc(collection(db, 'products'), productToSave);
      setProducts([...products, { id: docRef.id, ...productToSave }]);
      setIsEditing(false);
      setImageFile(null);
      setNewProduct({
        category: 'courses',
        categoryId: 'courses',
        categoryName: 'Cursos',
        title: '',
        desc: '',
        price: '',
        image: 'https://readdy.ai/api/search-image?query=digital%20product%20neon%20glow&width=400&height=280',
        cta: 'Comprar Ahora',
        link: 'https://stan.store',
        badge: ''
      });
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Error al crear el producto.');
    } finally {
      setUploading(false);
    }
  };

  const filtered = products.filter((p) => p.category === activeCategory);

  return (
    <section className="relative py-24 bg-[#030006] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(140,82,255,0.08)_0%,transparent_70%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-[#FF2DAA] text-xs font-bold uppercase tracking-widest mb-3">{t('home.products.badge')}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white">
              {t('home.products.titlePrefix')}{' '}
              <span className="bg-gradient-to-r from-[#FF2DAA] to-[#8c52ff] bg-clip-text text-transparent">
                {t('home.products.titleHighlight')}
              </span>
            </h2>
          </div>
          
          <div className="flex items-center gap-4">
            {isAdmin && (
              <button 
                onClick={() => setIsEditing(!isEditing)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
              >
                <i className={isEditing ? "ri-close-line" : "ri-add-line"} />
                {isEditing ? "Cancelar" : "Nuevo Producto"}
              </button>
            )}

            {/* Category Tabs */}
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full p-1">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer ${
                    activeCategory === cat.key
                      ? 'bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white'
                      : 'text-white/50 hover:text-white'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Formulario de Creación (Solo Admin) */}
        {isEditing && isAdmin && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-12">
            <h3 className="text-white font-bold text-xl mb-4">Crear Nuevo Producto</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs block mb-1">Categoría</label>
                <select 
                  value={newProduct.category}
                  onChange={(e) => setNewProduct({...newProduct, category: e.target.value as CategoryKey})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white focus:outline-none"
                >
                  {categories.map(c => (
                    <option key={c.key} value={c.key} className="bg-[#030006]">{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1">Título</label>
                <input 
                  type="text" 
                  value={newProduct.title}
                  onChange={(e) => setNewProduct({...newProduct, title: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white focus:outline-none"
                  placeholder="Ej: Curso de Instagram"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-white/50 text-xs block mb-1">Descripción</label>
                <textarea 
                  value={newProduct.desc}
                  onChange={(e) => setNewProduct({...newProduct, desc: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white focus:outline-none h-20"
                  placeholder="Describe el producto..."
                />
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1">Precio</label>
                <input 
                  type="text" 
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white focus:outline-none"
                  placeholder="Ej: $97"
                />
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1">Badge (Opcional)</label>
                <input 
                  type="text" 
                  value={newProduct.badge}
                  onChange={(e) => setNewProduct({...newProduct, badge: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white focus:outline-none"
                  placeholder="Ej: Más Vendido"
                />
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1">Subir Imagen</label>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files ? e.target.files[0] : null)}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white focus:outline-none text-sm"
                />
                <p className="text-white/30 text-xs mt-1">O deja en blanco para usar la imagen por defecto.</p>
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1">Link de Compra</label>
                <input 
                  type="text" 
                  value={newProduct.link}
                  onChange={(e) => setNewProduct({...newProduct, link: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white focus:outline-none"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button 
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                disabled={uploading}
              >
                Cancelar
              </button>
              <button 
                onClick={handleCreateProduct}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  "Crear Producto"
                )}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-white/50 py-10">
            <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
            Cargando productos...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-white/30 py-10 col-span-full">
            No hay productos en la categoría "{activeCategory}". Total productos cargados: {products.length}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((product) => (
            <div
              key={product.id}
              className="group relative bg-white/3 border border-white/8 rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-500 hover:-translate-y-2"
            >
              {/* Badge */}
              {product.badge && (
                <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white text-xs font-bold px-3 py-1 rounded-full">
                  {product.badge}
                </div>
              )}

              {/* Image */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030006] via-transparent to-transparent" />
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-white font-bold text-base leading-tight">{product.title}</h3>
                  <span className="text-[#00c2ff] font-black text-sm whitespace-nowrap">{product.price}</span>
                </div>
                <p className="text-white/50 text-xs leading-relaxed mb-4">{product.desc}</p>
                <div className="flex flex-col gap-2">
                  <div className="text-xs text-white/30 flex items-center gap-1">
                    <i className="ri-folder-line" />
                    {product.categoryName}
                  </div>
                  <a
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white text-sm font-semibold py-2.5 rounded-xl hover:opacity-90 transition-opacity duration-300 cursor-pointer"
                  >
                    {product.cta}
                    <i className="ri-external-link-line text-xs" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;
